import { Alert, Button, Divider, Input, Select } from 'antd';
import { EditorState } from 'draft-js';
import { Formik } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import RichTextEditor from '../../../foundation/components/rich_text_editor/RichTextEditor';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux';
import { sampleFormValidationSchema } from '../validation_schemas/sample_validation_schema';
import { useSampleModalLogic } from './hooks';

type SampleModalType = {
  closeModal: () => void;
};

const SampleModal = ({ closeModal }: SampleModalType) => {
  const lock = useSelector(selectVariantLock);
  const { canInteract, isReadOnly } = useVariantAccess();

  // All logic extracted to custom hook for separation of concerns
  const {
    isLoading,
    isDiagnosticumUser,
    zygosityOptions,
    segregationOptions,
    modalOpts,
    initialValues,
    handleFormSubmit,
  } = useSampleModalLogic({ closeModal });
  return (
    <Modal {...modalOpts} className="h-proband-modal">
      {isLoading && <FullPageLoader />}

      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={sampleFormValidationSchema(isDiagnosticumUser)}
      >
        {({
          errors,
          handleChange,
          values,
          handleSubmit,
          handleBlur,
          isValid,
          dirty,
          setFieldValue,
        }) => {
          return (
            <form onSubmit={handleSubmit}>
              <div className="c-form-field">
                <div className="c-form-field__label">Sample ID:</div>
                <div className="c-form-field__wrapper">
                  <Input
                    name="sample_id"
                    onChange={handleChange}
                    type="text"
                    value={values.sample_id}
                    onBlur={handleBlur}
                    placeholder="Enter sample ID"
                    readOnly={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {errors.sample_id ? errors.sample_id : undefined}
                  </div>
                </div>
              </div>
              <div className="c-form-field">
                <div className="c-form-field__label">Zygosity:</div>
                <div className="c-form-field__wrapper">
                  <Select
                    value={values.zygosity}
                    onChange={(v) => setFieldValue('zygosity', v)}
                    onBlur={handleBlur}
                    className="h-proband-modal__select-field"
                    options={zygosityOptions}
                    placeholder="Choose variant zygosity for sample"
                    disabled={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {errors.zygosity ? errors.zygosity : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Segregation:</div>
                <div className="c-form-field__wrapper">
                  <Select
                    value={values.segregation}
                    onChange={(v) => setFieldValue('segregation', v)}
                    onBlur={handleBlur}
                    className="h-proband-modal__select-field"
                    options={segregationOptions}
                    placeholder="Choose segregation for sample"
                    disabled={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {errors.segregation
                      ? (errors.segregation as any)
                      : undefined}
                  </div>
                </div>
              </div>

              <Divider />
              <div className="c-form-field">
                <div className="c-form-field__label">Comments:</div>
                <div className="c-form-field__wrapper">
                  <RichTextEditor
                    name="comment"
                    editorState={values.comment ?? EditorState.createEmpty()}
                    onChange={(newState) => setFieldValue('comment', newState)}
                    onBlur={() => handleBlur('comment')}
                    placeholder=""
                    label=""
                    readOnly={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {errors.comment ? errors.comment : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="h-proband-modal__form-submit-btn"
                  disabled={!(isValid && dirty) || isLoading || !canInteract}
                >
                  Create new sample
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default SampleModal;
